import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {assert} from 'Asserts';
import {removeGaps} from 'biojs/Util';
import {AntibodyNumberingLayout} from 'constants/MapsConstants';
import AntibodyAnalysisStore from 'molbio/antibody-engineering/antibodyAnalysis';
import {
  Numbering,
  NumberingScheme,
  ValidNumberingResult,
} from 'molbio/antibody-engineering/AntibodyEngineeringConstants';
import {getMetadataSummary} from 'molbio/antibody-engineering/useAntibodyMetadata';
import ComponentWrapper from 'molbio/maps/linear/ComponentWrapper';
import Type from 'molbio/maps/Type';
import {getRealToGapMap} from 'molbio/proteins/alignments/getRealToGapMap';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import {BLUE_LIGHTEST} from 'ui-library/Colors';

export const MIN_WIDTH_FOR_INDEX = 21;

/**
 * Given an index of an antibodyNumbering string, return the index of the alignment it should be rendered at,
 * accounting for numbering offset. This should only be used for alignments, which only have one slice,
 * and therefore don't care about the start of the slice, as it's always 0.
 */
export function getGappedIndexForNumber(
  numberingOffset: number,
  antibodyNumberIndex: number,
  realToGapMap: Array<number>
): number {
  return realToGapMap[antibodyNumberIndex + numberingOffset];
}

interface SliceNumberingInfo {
  // Antibody numbers shown on this slice
  indices: Array<string>;
  numberingStart: number; // Absolute index
  numberingEnd: number; // Absolute index
  // Residue index (not slice index) numbering starts at on this slice
  numberingStartForSlice: number;
  // Residue index (not slice index) numbering ends at on this slice
  numberingEndForSlice: number;
}

function getSlicedAntibodyNumbersForAlignments(
  numbering: Numbering,
  sliceStart: number,
  sliceEnd: number,
  realToGapped: Array<number>
): SliceNumberingInfo {
  const unGappedNumberingStart = numbering.start;
  const unGappedNumberingEnd = numbering.start + numbering.indices.length;
  const gappedStart = realToGapped[unGappedNumberingStart];
  // Handle edge case where numbering matches up exactly with end of residues
  const gappedEnd = realToGapped[unGappedNumberingEnd] || realToGapped[realToGapped.length - 1] + 1;

  return {
    indices: numbering.indices,
    numberingStart: gappedStart,
    numberingEnd: gappedEnd,
    numberingStartForSlice: gappedStart,
    numberingEndForSlice: gappedEnd,
  };
}

function getSlicedAntibodyNumbersForProteinMap(
  numbering: Numbering,
  sliceStart: number,
  sliceEnd: number
): SliceNumberingInfo {
  const absoluteIndexNumberingStart = numbering.start;
  const absoluteIndexNumberingEnd = numbering.start + numbering.indices.length;

  // Not rendering numbers in this slice
  if (absoluteIndexNumberingEnd < sliceStart || absoluteIndexNumberingStart >= sliceEnd) {
    return {
      indices: [],
      numberingStart: 0,
      numberingEnd: 0,
      numberingStartForSlice: 0,
      numberingEndForSlice: 0,
    };
  }

  const numberingStartForSlice = Math.max(sliceStart, absoluteIndexNumberingStart);
  const numberingEndForSlice = Math.min(sliceEnd, absoluteIndexNumberingEnd);
  const startIdx = Math.max(numberingStartForSlice - absoluteIndexNumberingStart, 0);
  const endIdx = Math.max(numberingEndForSlice - absoluteIndexNumberingStart, 0);
  const indices = numbering.indices.slice(startIdx, endIdx);
  return {
    indices,
    numberingStart: absoluteIndexNumberingStart,
    numberingEnd: absoluteIndexNumberingEnd,
    numberingStartForSlice,
    numberingEndForSlice,
  };
}

interface AntibodyNumberProps {
  value: string;
  gappedIndex: number;
  xFn: Props['xFn'];
  layout: Props['layout'];
}

/**
 * Renders a single antibody index at the correct horizontal position
 */
function AntibodyNumber(props: AntibodyNumberProps): JSX.Element {
  const {value, gappedIndex, xFn, layout} = props;
  const xPos = xFn(gappedIndex);

  const type = new Type(layout.type.fontSize);
  const totalWidth = xFn(gappedIndex + 1) - xPos;
  const textWidth = type.width(value.length);
  const xPad = (totalWidth - textWidth) / 2;

  return <text transform={`translate(${xPos + xPad} 6)`}>{value}</text>;
}

interface AntibodyChainProps {
  layout: AntibodyNumberingLayout;
  numbering: ValidNumberingResult;
  onAlignment: boolean;
  realToGapped: Array<number>;
  setSelection: (start: number, end: number) => void;
  sliceStart: number;
  // Exclusive
  sliceEnd: number;
  // A function that takes the index (counting gaps) of a residue and returns its x-axis position on the slice
  xFn: (residueIndex: number) => number;
}

function AntibodyChain(props: AntibodyChainProps): JSX.Element {
  const {setSelection, xFn, numbering, sliceStart, sliceEnd, realToGapped, onAlignment, layout} = props;

  const {indices, numberingStart, numberingEnd, numberingEndForSlice, numberingStartForSlice} = onAlignment
    ? getSlicedAntibodyNumbersForAlignments(numbering, sliceStart, sliceEnd, realToGapped)
    : getSlicedAntibodyNumbersForProteinMap(numbering, sliceStart, sliceEnd);

  // Cannot be replaced with xFn(1) - xFn(0) because xFn is specific to the slice and may not have 1 and 0 in its domain
  const zoomedIn = xFn(numberingStartForSlice + 1) - xFn(numberingStartForSlice) > MIN_WIDTH_FOR_INDEX;
  const tooltip = zoomedIn
    ? getMetadataSummary(numbering)
    : onAlignment
      ? 'Zoom in using the minimap to see antibody numbering'
      : 'Zoom in to see antibody numbering';

  return (
    <>
      <TooltipWrapper tooltipTitle={tooltip}>
        <rect
          onClick={() => {
            setSelection(numberingStart, numberingEnd);
          }}
          className={`layer-fill ${css(styles.noFocus)}`}
          fill={BLUE_LIGHTEST}
          fillOpacity={0.7}
          x={xFn(numberingStartForSlice)}
          y={zoomedIn ? -5 : 0}
          width={xFn(numberingEndForSlice) - xFn(numberingStartForSlice)}
          height={zoomedIn ? 15 : 5}
        />
      </TooltipWrapper>
      {zoomedIn &&
        indices.map((value, i) => (
          <AntibodyNumber
            key={value}
            gappedIndex={
              onAlignment
                ? getGappedIndexForNumber(numbering.start, i, realToGapped)
                : numberingStartForSlice + i
            }
            {...{value, xFn, layout}}
          />
        ))}
    </>
  );
}

export interface Props {
  layout: AntibodyNumberingLayout;
  onAlignment: boolean;
  redraw: (boxHeight: number) => void;
  residues: string;
  scheme: string;
  setSelection: (start: number, end: number) => void;
  sliceStart: number;
  // Exclusive
  sliceEnd: number;
  // A function that takes the index (counting gaps) of a residue and returns its x-axis position on the slice
  xFn: (residueIndex: number) => number;
}

/**
 * Displays indices beneath the portion of the slice that matches an identifiable antibody chain.
 * At wider zoom levels, only shows a rectangle with tooltip in place of individual indices.
 */
export default function AntibodyNumberingSlice({
  layout,
  onAlignment,
  redraw,
  residues,
  scheme,
  setSelection,
  sliceStart,
  sliceEnd,
  xFn,
}: Props): JSX.Element | null {
  assert(onAlignment ? sliceStart === 0 : true, 'Alignments only have one slice, so slice must start at 0');

  const boxHeight = Type.heights[layout.type.fontSize] + layout.vMargin;
  const nonGappedResidues = removeGaps(residues);
  const realToGapped = getRealToGapMap(residues);
  const [numberings, setNumberings] = useState<Array<ValidNumberingResult> | null>(null);
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const fetchNumbering = async () => {
      const [response] = await AntibodyAnalysisStore.getNumbering(
        [nonGappedResidues],
        scheme.toLowerCase() as NumberingScheme
      );
      setNumberings(response);
      redraw(response ? boxHeight : 0);
    };
    fetchNumbering();
  }, [boxHeight, nonGappedResidues, scheme, redraw]);

  if (!numberings) {
    return null;
  }

  return (
    <ComponentWrapper layout={layout} shouldStopMouseDownPropagation={false}>
      <g fontSize={layout.type.fontSize} className={css(styles.noFocus)}>
        {numberings.map((numbering) => (
          <AntibodyChain
            key={numbering.start}
            {...{setSelection, xFn, numbering, sliceStart, sliceEnd, realToGapped, onAlignment, layout}}
          />
        ))}
      </g>
    </ComponentWrapper>
  );
}

const styles = StyleSheet.create({
  noFocus: {
    ':focus': {
      outline: 'none',
    },
  },
});
